/**
 * PoVoidDialog — capture an optional reason while voiding an issued PO.
 *
 * A `sent` / `confirmed` purchase order is voided (vendor declined, duplicate,
 * wrong terms) by moving it to the terminal `cancelled` status. The reason is
 * optional per the wire contract but lands on the append-only audit trail, so
 * we nudge for one. Mirrors the shape of `invoices/request-void-dialog.tsx`.
 *
 * @see packages/api-contract/src/procedures/purchase-orders.ts — voidInput
 */
import { useEffect, useState } from "react";
import { Ban } from "lucide-react";
import {
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Label,
} from "@flint/ui";

export interface PoVoidDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `PO/2026-27/0007` — used in the body copy. */
  poNumber: string | null;
  /** Formatted PO total (e.g. `₹64,428`). */
  totalLabel: string;
  vendorName: string;
  pending?: boolean;
  errorMessage?: string;
  /** Fires with a trimmed reason or `null` when the field is left empty. */
  onConfirm: (payload: { reason: string | null }) => void;
}

export function PoVoidDialog({
  open,
  onOpenChange,
  poNumber,
  totalLabel,
  vendorName,
  pending,
  errorMessage,
  onConfirm,
}: PoVoidDialogProps) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm({ reason: reason.trim() || null });
  };

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <form onSubmit={handleSubmit} noValidate>
          <DialogHeader>
            <div className="flex items-start gap-3">
              <span
                aria-hidden
                className="inline-grid h-9 w-9 shrink-0 place-items-center rounded-md bg-bad-50 text-bad"
              >
                <Ban size={16} />
              </span>
              <div className="flex-1">
                <DialogTitle>Void this purchase order?</DialogTitle>
                <DialogDescription>
                  {poNumber ?? "This order"} to {vendorName} ({totalLabel}) will move to{" "}
                  <strong>cancelled</strong>. The row stays as a historical record — you can't
                  un-void it. Drafts are deleted instead; this path is for issued orders.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="mt-4 flex flex-col gap-1.5">
            <Label htmlFor="po-void-reason">Reason (optional)</Label>
            <textarea
              id="po-void-reason"
              autoFocus
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder="e.g. Vendor can't meet the delivery date — re-ordering elsewhere."
              className="min-h-[72px] w-full resize-y rounded-sm border border-line bg-paper px-3 py-2 text-sm leading-relaxed text-ink outline-none transition-colors placeholder:text-muted hover:border-line-2 focus-visible:border-sky-600 focus-visible:ring-2 focus-visible:ring-sky-100"
            />
            <p className="text-sm text-muted">Recorded on the audit trail next to the void.</p>
            {errorMessage ? <p className="mt-1 text-sm text-bad">{errorMessage}</p> : null}
          </div>

          <div className="mt-5 flex items-center justify-end gap-2">
            <Button type="button" intent="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
              Keep order
            </Button>
            <Button type="submit" intent="danger" disabled={pending}>
              {pending ? "Voiding…" : "Void order"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}
